import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { registrosApi } from '@/modules/registros/api/registros.api'
import type { ConfiguracionRegistros, Grupo, TestConexionResponse } from '../types'

export function useConfiguraciones(params?: {
  page?: number
  page_size?: number
  search?: string
  activo?: boolean
  grupo_id?: number
}) {
  return useQuery({
    queryKey: ['configuraciones', params],
    queryFn: () => registrosApi.getConfiguraciones(params),
  })
}

export function useConfiguracion(id: number) {
  return useQuery({
    queryKey: ['configuracion', id],
    queryFn: () => registrosApi.getConfiguracion(id),
    enabled: !!id,
  })
}

export function useCreateConfiguracion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: Partial<ConfiguracionRegistros>) =>
      registrosApi.createConfiguracion(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuraciones'] })
    },
  })
}

export function useUpdateConfiguracion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<ConfiguracionRegistros> }) =>
      registrosApi.updateConfiguracion(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuraciones'] })
      queryClient.invalidateQueries({ queryKey: ['configuracion'] })
    },
  })
}

export function useDeleteConfiguracion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => registrosApi.deleteConfiguracion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuraciones'] })
    },
  })
}

export function useToggleActivo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => registrosApi.toggleActivo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuraciones'] })
    },
  })
}

export function useTestConexion() {
  return useMutation<
    TestConexionResponse,
    Error,
    {
      db_host: string
      db_port: string
      db_name: string
      db_user: string
      db_password?: string
      db_engine: string
      tabla_nombre?: string
      config_id?: number
    }
  >({
    mutationFn: (payload) => registrosApi.testConexion(payload),
  })
}

export function useGrupos() {
  return useQuery<Grupo[]>({
    queryKey: ['grupos'],
    queryFn: () => registrosApi.getGrupos(),
  })
}

// Hooks para Marcadores Especiales (Hitos)
export function useMarcadores(configId: number) {
  return useQuery({
    queryKey: ['marcadores', configId],
    queryFn: () => registrosApi.getMarcadores(configId),
    enabled: !!configId,
  })
}

export function useCreateMarcador() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: {
      configuracion_id: number
      fecha: string
      titulo: string
      descripcion?: string
      url_reporte?: string
      tipo: string
      color: string
      activo: boolean
      ultimo_hito: boolean
      primer_hito: boolean
    }) => registrosApi.createMarcador(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['marcadores', variables.configuracion_id] })
      queryClient.invalidateQueries({ queryKey: ['reporte-graficos', variables.configuracion_id] })
    },
  })
}

export function useUpdateMarcador() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      marcadorId,
      payload,
    }: {
      marcadorId: number
      configId: number
      payload: {
        fecha?: string
        titulo?: string
        descripcion?: string
        url_reporte?: string
        tipo?: string
        color?: string
        activo?: boolean
        ultimo_hito?: boolean
        primer_hito?: boolean
      }
    }) => registrosApi.updateMarcador(marcadorId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['marcadores', variables.configId] })
      queryClient.invalidateQueries({ queryKey: ['reporte-graficos', variables.configId] })
    },
  })
}

export function useDeleteMarcador() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ marcadorId }: { marcadorId: number; configId: number }) =>
      registrosApi.deleteMarcador(marcadorId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['marcadores', variables.configId] })
      queryClient.invalidateQueries({ queryKey: ['reporte-graficos', variables.configId] })
    },
  })
}
